// services/reportService.js
import api from './api';

/**
 * Сервис для работы с отчетами
 */
const reportService = {
  /**
   * Получение статистики для дашборда
   * @returns {Promise<Object>} - Данные статистики
   */
  getDashboardStats: async () => {
    const response = await api.get('/reports/dashboard-stats');
    return response.data;
  },

/**
 * Получение истории цен на товар
 * @param {string} productId - ID товара
 * @param {string} startDate - Начальная дата (опционально)
 * @param {string} endDate - Конечная дата (опционально)
 * @returns {Promise<Array>} - История цен
 */
getProductPriceHistory: async (productId, startDate, endDate) => {
  let url = `/reports/products/${productId}/prices`;
  const params = [];
  
  if (startDate) {
    // Используем тот же метод, что и в TaskFormPage.js
    const startLocalDate = new Date(startDate + 'T00:00:00');
    params.push(`startDate=${startLocalDate.toISOString()}`);
  }
  
  if (endDate) {
    // Для конечной даты установим 23:59:59
    const endLocalDate = new Date(endDate + 'T23:59:59');
    params.push(`endDate=${endLocalDate.toISOString()}`);
  }
  
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  
  const response = await api.get(url);
  return response.data;
},

/**
 * Получение истории цен в магазине
 * @param {number} storeId - ID магазина
 * @param {string} startDate - Начальная дата (опционально)
 * @param {string} endDate - Конечная дата (опционально)
 * @returns {Promise<Array>} - История цен
 */
getStorePriceHistory: async (storeId, startDate, endDate) => {
  let url = `/reports/stores/${storeId}/prices`;
  const params = [];
  
  if (startDate) {
    // Используем тот же метод, что и в TaskFormPage.js
    const startLocalDate = new Date(startDate + 'T00:00:00');
    params.push(`startDate=${startLocalDate.toISOString()}`);
  }
  
  if (endDate) {
    // Для конечной даты установим 23:59:59
    const endLocalDate = new Date(endDate + 'T23:59:59');
    params.push(`endDate=${endLocalDate.toISOString()}`);
  }
  
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  
  const response = await api.get(url);
  return response.data;
},

  /**
   * Получение аналитики по категориям
   * @returns {Promise<Array>} - Данные по категориям
   */
  getCategoryAnalytics: async () => {
    const response = await api.get('/reports/categories/analytics');
    return response.data;
  },

  /**
 * Получение сводки по ценникам
 * @param {string} startDate - Начальная дата (опционально)
 * @param {string} endDate - Конечная дата (опционально)
 * @returns {Promise<Object>} - Сводка по ценникам
 */
getPriceTagsSummary: async (startDate, endDate) => {
  let url = '/reports/price-tags/summary';
  const params = [];
  
  if (startDate) {
    // Используем тот же метод, что и в TaskFormPage.js
    const startLocalDate = new Date(startDate + 'T00:00:00');
    params.push(`startDate=${startLocalDate.toISOString()}`);
  }
  
  if (endDate) {
    // Для конечной даты установим 23:59:59
    const endLocalDate = new Date(endDate + 'T23:59:59');
    params.push(`endDate=${endLocalDate.toISOString()}`);
  }
  
  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }
  
  const response = await api.get(url);
  return response.data;
},

  /**
   * Подготовка данных для отчета "Динамика цен"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные для отчета
   */
  preparePriceDynamicsReport: async (filters) => {
    try {
      // Если выбран товар, получаем историю цен для этого товара
      if (filters.productId) {
        const priceHistory = await reportService.getProductPriceHistory(
          filters.productId, 
          filters.startDate, 
          filters.endDate
        );
        
        // Преобразуем данные в формат для отчета
        const data = {
          labels: [],
          datasets: []
        };
        
        // Группируем данные по магазинам
        const storeGroups = {};
        
        priceHistory.forEach(item => {
          const date = new Date(item.recordedAt).toLocaleDateString();
          
          if (!data.labels.includes(date)) {
            data.labels.push(date);
          }
          
          if (!storeGroups[item.store.name]) {
            storeGroups[item.store.name] = {
              name: `${item.product.name} (${item.store.name})`,
              data: {}
            };
          }
          
          storeGroups[item.store.name].data[date] = item.price;
        });
        
        // Преобразуем группы в наборы данных
        Object.values(storeGroups).forEach(store => {
          const prices = data.labels.map(date => store.data[date] || null);
          data.datasets.push({
            name: store.name,
            data: prices
          });
        });
        
        return data;
      } 
      // Если выбран магазин, получаем историю цен для этого магазина
      else if (filters.storeId) {
        const priceHistory = await reportService.getStorePriceHistory(
          filters.storeId,
          filters.startDate,
          filters.endDate
        );
        
        // Преобразуем данные в формат для отчета
        const data = {
          labels: [],
          datasets: []
        };
        
        // Группируем данные по товарам
        const productGroups = {};
        
        priceHistory.forEach(item => {
          const date = new Date(item.recordedAt).toLocaleDateString();
          
          if (!data.labels.includes(date)) {
            data.labels.push(date);
          }
          
          if (!productGroups[item.product.id]) {
            productGroups[item.product.id] = {
              name: item.product.name,
              data: {}
            };
          }
          
          productGroups[item.product.id].data[date] = item.price;
        });
        
        // Преобразуем группы в наборы данных
        Object.values(productGroups).forEach(product => {
          const prices = data.labels.map(date => product.data[date] || null);
          data.datasets.push({
            name: product.name,
            data: prices
          });
        });
        
        return data;
      }
      // Если ничего не выбрано, возвращаем пустые данные
      else {
        return {
          labels: [],
          datasets: []
        };
      }
    } catch (error) {
      console.error('Ошибка при подготовке отчета по динамике цен:', error);
      throw error;
    }
  },

  /**
   * Подготовка данных для отчета "Сравнение магазинов"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные для отчета
   */
  prepareStoreComparisonReport: async (filters) => {
    try {
      // Получаем список магазинов
      const storesResponse = await api.get('/stores');
      const stores = storesResponse.data;
      
      // Если выбран товар, получаем цены на этот товар в разных магазинах
      if (filters.productId) {
        const product = await api.get(`/products/${filters.productId}`);
        const productName = product.data.name;
        
        // Получаем ценники для этого товара
        const priceTagsResponse = await api.get(`/price-tags?productId=${filters.productId}`);
        const priceTags = priceTagsResponse.data;
        
        // Группируем ценники по магазинам
        const storeGroups = {};
        
        priceTags.forEach(tag => {
          if (!storeGroups[tag.storeId]) {
            const store = stores.find(s => s.id === tag.storeId);
            storeGroups[tag.storeId] = {
              id: tag.storeId,
              name: store ? store.name : `Магазин #${tag.storeId}`,
              price: tag.price
            };
          } else if (tag.createdAt > storeGroups[tag.storeId].createdAt) {
            // Если есть более новый ценник, обновляем цену
            storeGroups[tag.storeId].price = tag.price;
          }
        });
        
        return {
          product: productName,
          stores: Object.values(storeGroups).map(s => s.name),
          products: [{
            name: productName,
            prices: Object.values(storeGroups).map(s => s.price)
          }]
        };
      } 
      // Если выбрана категория, получаем товары из этой категории и их цены
      else if (filters.categoryId) {
        const productsResponse = await api.get(`/products?categoryId=${filters.categoryId}`);
        const products = productsResponse.data;
        
        // Для простоты ограничимся 5 товарами
        const selectedProducts = products.slice(0, 5);
        
        const storeIds = stores.map(s => s.id);
        const storeNames = stores.map(s => s.name);
        
        const productPrices = [];
        
        // Для каждого товара получаем цены в разных магазинах
        for (const product of selectedProducts) {
          const priceTagsResponse = await api.get(`/price-tags?productId=${product.id}`);
          const priceTags = priceTagsResponse.data;
          
          // Группируем ценники по магазинам
          const storeGroups = {};
          
          priceTags.forEach(tag => {
            if (!storeGroups[tag.storeId]) {
              storeGroups[tag.storeId] = {
                id: tag.storeId,
                price: tag.price,
                createdAt: new Date(tag.createdAt)
              };
            } else if (new Date(tag.createdAt) > storeGroups[tag.storeId].createdAt) {
              // Если есть более новый ценник, обновляем цену
              storeGroups[tag.storeId].price = tag.price;
              storeGroups[tag.storeId].createdAt = new Date(tag.createdAt);
            }
          });
          
          // Формируем массив цен в соответствии с порядком магазинов
          const prices = storeIds.map(id => {
            return storeGroups[id] ? storeGroups[id].price : null;
          });
          
          productPrices.push({
            name: product.name,
            prices: prices
          });
        }
        
        return {
          stores: storeNames,
          products: productPrices
        };
      }
      // Если ничего не выбрано, возвращаем пустые данные
      else {
        return {
          stores: [],
          products: []
        };
      }
    } catch (error) {
      console.error('Ошибка при подготовке отчета по сравнению магазинов:', error);
      throw error;
    }
  },

  /**
   * Получение отчета "Сравнение магазинов"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные отчета
   */
  getStoreComparisonReport: async (filters = {}) => {
    let url = '/reports/store-comparison';
    const params = [];
    
    if (filters.productId) {
      params.push(`productId=${filters.productId}`);
    }
    
    if (filters.categoryId) {
      params.push(`categoryId=${filters.categoryId}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Получение отчета "Сравнение цен"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные отчета
   */
  getPriceComparisonReport: async (filters = {}) => {
    let url = '/reports/price-comparison';
    const params = [];
    
    if (filters.productId) {
      params.push(`productId=${filters.productId}`);
    }
    
    if (filters.categoryId) {
      params.push(`categoryId=${filters.categoryId}`);
    }
    
    if (filters.storeId) {
      params.push(`storeId=${filters.storeId}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Получение отчета "Активность пользователей"
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Данные отчета
   */
  getUserActivityReport: async (filters = {}) => {
    let url = '/reports/user-activity';
    const params = [];
    
    if (filters.startDate) {
      const startLocalDate = new Date(filters.startDate + 'T00:00:00');
      params.push(`startDate=${startLocalDate.toISOString()}`);
    }
    
    if (filters.endDate) {
      const endLocalDate = new Date(filters.endDate + 'T23:59:59');
      params.push(`endDate=${endLocalDate.toISOString()}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Получение аналитики по единицам измерения
   * @returns {Promise<Object>} - Статистика единиц измерения
   */
  getUnitsAnalytics: async () => {
    const response = await api.get('/reports/units-analytics');
    return response.data;
  },

  /**
   * Получение аналитики по изображениям
   * @param {Object} filters - Фильтры отчета
   * @returns {Promise<Object>} - Статистика изображений
   */
  getImagesAnalytics: async (filters = {}) => {
    let url = '/reports/images-analytics';
    const params = [];
    
    if (filters.startDate) {
      const startLocalDate = new Date(filters.startDate + 'T00:00:00');
      params.push(`startDate=${startLocalDate.toISOString()}`);
    }
    
    if (filters.endDate) {
      const endLocalDate = new Date(filters.endDate + 'T23:59:59');
      params.push(`endDate=${endLocalDate.toISOString()}`);
    }
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Экспорт отчета в файл
   * @param {string} reportType - Тип отчета
   * @param {string} format - Формат файла (csv, excel, pdf)
   * @returns {Promise} - Скачивание файла
   */
  exportReport: async (reportType, format) => {
    const response = await api.get(`/reports/export/${reportType}?format=${format}`, {
      responseType: 'blob'
    });
    
    // Создаем ссылку для скачивания
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${reportType}_${new Date().toISOString().slice(0, 10)}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: `Отчет ${reportType} успешно экспортирован в формате ${format}` };
  }
};

// Экспортируем отдельные функции для именованного импорта
export const getImagesAnalytics = reportService.getImagesAnalytics;
export const getUnitsAnalytics = reportService.getUnitsAnalytics;

export default reportService;